import { Currencies } from "@/constants/currency";
import { IReceiptPayment } from "@/interfaces/receipts";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { roundToNearestMultipleOf5 } from "../helpers/common";
import {
  OrderSession,
  Payment,
  PaymentMethod,
  PaymentState,
  TransactionType,
} from "@smeetz/orders";

/**
 * Resolve the printed name for a payment method. Swiss QR and Incert voucher use
 * dedicated i18n keys; every other method maps to `payment-method.<METHOD>`.
 */
function paymentMethodName(paymentMethod: PaymentMethod | string): string {
  if (paymentMethod === PaymentMethod.Swiss_QR_Code_Bank_Transfer) {
    return t("payment-method.swiss-qr-code-bank-transfer");
  }
  if (paymentMethod === PaymentMethod.EXTERNAL_INCERT_VOUCHER) {
    return t("payment-method.Incert_Voucher");
  }
  return t(`payment-method.${paymentMethod}`);
}

// Only authorized or completed payments took money; declined, aborted and in-flight attempts stay on the session unprinted.
function isSettledPayment(payment: Payment): boolean {
  return (
    payment.state === PaymentState.AUTHORIZED ||
    payment.state === PaymentState.COMPLETED
  );
}

/**
 * @param orderSession the order session whose payments/refunds are printed
 * @returns payments done in the order session
 */
export function getReceiptPayments(
  orderSession: OrderSession
): IReceiptPayment[] {
  const receiptPayments: IReceiptPayment[] = [];
  const { payments } = orderSession;

  // 1- Handle payments accountings
  for (const payment of orderSession.payments) {
    if (
      payment.paymentMethod === PaymentMethod.Swiss_QR_Code_Bank_Transfer &&
      payment.state !== PaymentState.COMPLETED
    ) {
      continue;
    }
    if (
      payment.type === TransactionType.TAX ||
      payment.type === TransactionType.DISCOUNT ||
      payment.type === TransactionType.TICKET ||
      payment.type === TransactionType.REFUND ||
      // FIS-715: a terminal gratuity is a Payment row on the session, but it is
      // not a payment *of the order* — printing it here would add a second line
      // to every tipped receipt. Tips print only on the @smeetz/receipt render
      // (Tip / Total charged rows, behind pos::unified-receipt); the legacy
      // template deliberately leaves them out.
      payment.type === TransactionType.TIP ||
      payment.paymentMethod === PaymentMethod.FREE ||
      !isSettledPayment(payment)
    ) {
      continue;
    }
    if (
      payment.paymentMethod === PaymentMethod.CASH &&
      payment.multiCurrencyInfo?.paymentCurrency &&
      payment.currency !== payment.multiCurrencyInfo?.paymentCurrency &&
      payment.multiCurrencyInfo?.providedCashBill
    ) {
      /*
       if payment was in secondary currency we print the provided
       cash bill in that currency and change given back in main currency
      */
      const { multiCurrencyInfo } = payment;
      let value =
        multiCurrencyInfo.providedCashBill *
        (1 / (multiCurrencyInfo.exchangeRate || 1));
      if (payment.currency === Currencies.CHF) {
        value = roundToNearestMultipleOf5(Math.round(value));
      }
      receiptPayments.push({
        name: paymentMethodName(payment.paymentMethod),
        providedCashBill: (multiCurrencyInfo.providedCashBill / 100).toFixed(2),
        exchangeRate: (multiCurrencyInfo.exchangeRate || 1).toFixed(2),
        value: (value / 100).toFixed(2),
        currency:
          multiCurrencyInfo.paymentCurrency || payment.currency || "CHF",
      });
    } else if (
      payment.paymentMethod === PaymentMethod.CASH &&
      payment.multiCurrencyInfo?.paymentCurrency &&
      payment.currency === payment.multiCurrencyInfo.paymentCurrency &&
      payment.multiCurrencyInfo.paymentCurrency
    ) {
      const { multiCurrencyInfo } = payment;
      // if paid in cash with main currency then
      // print the provided cash bill of the main currency
      // and the change given back (if any) by substracting the accounting entry values
      const { value } = payment;
      receiptPayments.push({
        name: paymentMethodName(payment.paymentMethod),
        value: (multiCurrencyInfo.providedCashBill / 100).toFixed(2),
        currency:
          multiCurrencyInfo.paymentCurrency || payment.currency || "CHF",
      });
      const changeValue = multiCurrencyInfo.providedCashBill - value;
      if (changeValue > 0) {
        // only print if there was a change
        receiptPayments.push({
          name: t(`multicurrencies.change`),
          value: ((changeValue * -1) / 100).toFixed(2),
          currency: payment.currency || "CHF",
        });
      }
    } else {
      const { multiCurrencyInfo } = payment;
      // otherwise print the payment method with value of accounting entries
      const { value } = payment;
      receiptPayments.push({
        name: paymentMethodName(payment.paymentMethod),
        value: (value / 100).toFixed(2),
        currency:
          multiCurrencyInfo?.paymentCurrency || payment.currency || "CHF",
      });
    }
  }
  // 2- Now handle refund acountings
  const refunds = payments.filter(
    (payment) =>
      payment.type === TransactionType.REFUND && isSettledPayment(payment)
  );
  const groupedRefunds: {
    [method: string]: Payment[];
  } = {};
  // Lets group refund accountings based on their paymentMethods
  for (const refund of refunds) {
    if (groupedRefunds[refund.paymentMethod]) {
      // This payment method is already grouped lets push this ac
      groupedRefunds[refund.paymentMethod].push(refund);
    } else {
      groupedRefunds[refund.paymentMethod] = [];
      groupedRefunds[refund.paymentMethod].push(refund);
    }
  }
  // Now foreach grouped accountings we will calculate the sum of their accounting entries
  for (const paymentMethod in groupedRefunds) {
    let value = 0;
    groupedRefunds[paymentMethod]
      .map((refund) => refund.value)
      .forEach((refundValue) => {
        value += refundValue;
      });
    receiptPayments.push({
      name: paymentMethodName(paymentMethod),
      value: (value / 100).toFixed(2),
      currency: groupedRefunds[paymentMethod][0].currency || "CHF",
    });
  }
  return receiptPayments;
}

// CHF subunits are Rappen/Centimes/Centesimi, never "Cents"; generic label where a language leaves the CHF key empty.
export function getCentsLabel(currency: string): string {
  const te: (key: string) => boolean = (i18n.global as any).te;
  if (currency === Currencies.CHF && te("cashfloat.cents-chf")) {
    return t("cashfloat.cents-chf") || t("cashfloat.cents");
  }
  return t("cashfloat.cents");
}
